import { useQuoteI18n } from '../quoteI18n'

/**
 * Attesa di un passaggio asincrono dell'Executor, con il dettaglio di cosa sta accadendo.
 *
 * Le due attese si assomigliano ma vogliono dire cose diverse, e vanno distinte perché
 * dopo la prima può arrivare un modulo da compilare mentre dopo la seconda no:
 *
 * - `preparing`: i servizi sono stati inviati, il viaggio si sta creando e stiamo
 *   scoprendo quali dati il fornitore richiede. **Non è una conferma.**
 * - `finalizing`: i dati sono stati inviati, si sta concludendo la prenotazione.
 *
 * @param {Object} props
 * @param {'preparing'|'finalizing'} props.variant - Quale delle due attese
 * @param {boolean} [props.stalled] - True quando l'attesa ha superato il timeout
 */
export function WaitingPanel({ variant, stalled }) {
  const { t } = useQuoteI18n()
  const preparing = variant === 'preparing'

  // I tre passi sono sempre gli stessi e nello stesso ordine: fatto, in corso, da fare.
  const labels = preparing ? t.waiting.preparingSteps : t.waiting.finalizingSteps
  const steps = labels.map((label, index) => ({
    label,
    state: index === 0 ? 'done' : index === 1 ? 'current' : 'todo'
  }))

  if (stalled) {
    return (
      <div className="gq-panel gq-panel--warning" role="status">
        <div className="gq-panel__icon">⏳</div>
        <h3 className="gq-panel__title">{t.waiting.stalledTitle}</h3>
        <p className="gq-panel__text">{t.waiting.stalledText}</p>
        <p className="gq-panel__hint">{t.waiting.stalledHint}</p>
      </div>
    )
  }

  return (
    <div className="gq-panel" role="status" aria-live="polite">
      <div className="gq-spinner" />
      <h3 className="gq-panel__title">
        {preparing ? t.waiting.preparingTitle : t.waiting.finalizingTitle}
      </h3>
      <p className="gq-panel__text">
        {preparing ? t.waiting.preparingText : t.waiting.finalizingText}
      </p>

      <ul className="gq-progress-list">
        {steps.map((step) => (
          <li key={step.label} data-state={step.state}>
            <span className="gq-progress-list__dot" aria-hidden="true">
              {step.state === 'done' ? '✓' : step.state === 'current' ? '•' : '○'}
            </span>
            {step.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
